import {
  Fragment,
  forwardRef,
  useEffect,
  useRef,
  useState, // useCallback,
} from 'react';

import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import useLanguage from '@snowit/system/useLanguage';
import executer from '@snowit/utils/executer';

import Button from '../../Buttons/Button';
import ButtonIcon from '../../Buttons/ButtonIcon';
import { m } from '../../utils/Motion';
import Input from '../Input';
import InputSpinner from '../InputSpinner/InputSpinner';

import cx from 'classnames';
import s from './InputSelectSpinner.module.scss';

export type InputSelectSpinnerOption = {
  key: string;
  name?: string;
  disabled?: boolean;
  label?: string;
  sublabel?: string;
  value: number;
  minValue?: number;
  maxValue?: number;
};
export interface IInputSelectSpinner {
  dropdownClassName?: string;
  inputClassName?: string;
  hint?: string;
  hintOptions?: string;
  error?: boolean;
  errorMsg?: string;
  label?: string;
  onChange: (
    value: number,
    option: InputSelectSpinnerOption,
    options: InputSelectSpinnerOption[],
    parent: { name?: string; id?: string },
  ) => void;
  placeholder?: string;
  value?: string;
  options: InputSelectSpinnerOption[];
  disabled?: boolean;
  id?: string;
  name?: string;
  test?: string;
}

const InputSelectSpinner = forwardRef((props: IInputSelectSpinner, ref: any) => {
  const {
    dropdownClassName,
    inputClassName,
    disabled,
    error,
    errorMsg,
    hint,
    hintOptions,
    id,
    label,
    name,
    onChange,
    options = [],
    placeholder,
    test,
    value,
  } = props;

  const { t } = useLanguage();
  const parent = useRef(null);
  const { minWidth } = useBreakpoints();

  const [showDropdown, setShowDropdown] = useState(false);

  const handleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    if (ref?.current) {
      ref.current = parent.current;
    }
  }, [ref?.current, parent.current]);

  const handleChange = (newValue: number, option: InputSelectSpinnerOption) => {
    onChange(newValue, option, options, { name, id });
    // executer(onChange, { ...args, option, options, parent: { name, id } });
  };

  const renderModalHeader = () => {
    if (minWidth.sm) return null;
    return (
      <header className={s.header}>
        <h2 className={s.title}>{placeholder ? placeholder : label}</h2>
        <ButtonIcon
          classNames={{ button: s.close }}
          icon={'close'}
          onClick={() => setShowDropdown(false)}
          theme={'ghost'}
          size={'db'}
          color={'text'}
        />
      </header>
    );
  };

  const renderModalFooter = () => {
    if (minWidth.sm) return null;
    return (
      <div className={s.footer}>
        <Button
          classNames={{ button: s.btn }}
          label={t('lzy.action.confirm')}
          theme={'fill'}
          bgColor={'primary'}
          onClick={() => setShowDropdown(false)}
        />
      </div>
    );
  };

  let motionVariants = {};

  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateY: '0%' },
      collapsed: { opacity: 1, translateY: '100%' },
    };
  }

  const Dropdown = (
    <m.ul
      // positionTransition
      className={cx(s.dropDown, dropdownClassName)}
      initial='collapsed'
      animate='open'
      exit='collapsed'
      variants={motionVariants}
      transition={{
        type: 'tween',
        duration: 0.25,
        ease: [0.215, 0.61, 0.355, 1],
      }}
    >
      {renderModalHeader()}

      <div className={s.body}>
        {options.map((option) => (
          <li className={s.option} key={option.key}>
            <InputSpinner
              className={s.spinner}
              id={option.key}
              name={option.name || option.key}
              label={option.label}
              sublabel={option.sublabel}
              value={option.value}
              maxValue={option.maxValue}
              minValue={option.minValue}
              onChange={(newValue) => handleChange(newValue, option)}
              disabled={option.disabled}
              test={`${test}_${option.key}`}
            />
          </li>
        ))}

        {hintOptions && <p className={s.hint}>{hintOptions}</p>}
      </div>

      {renderModalFooter()}

      {/* {error && <p className={s.error}>{errorMsg}</p>} */}
    </m.ul>
  );

  return (
    <Fragment>
      <Input
        classNames={{ wrapper: inputClassName }}
        dropdown
        fakeInput
        label={label}
        onClick={handleDropdown}
        placeholder={placeholder}
        ref={parent}
        type={'text'}
        onChange={() => false}
        value={executer(value)}
        error={error}
        errorMsg={errorMsg}
        disabled={disabled}
        test={test}
        hint={hint}
      />
      <Portal
        mounted={showDropdown}
        content={Dropdown}
        bindTo={parent.current}
        onClose={() => setShowDropdown(false)}
        fullscreen={!minWidth.sm}
        clickToHide
        position={'adaptive'}
      />
    </Fragment>
  );
});

export default InputSelectSpinner;
